import { useEffect, useRef } from 'react';
import useWorldStore from '../store/worldStore';
import { ZONES } from '../data/zones';
import { ACTIVITIES } from './activityConfig';

const ZONE_TASKS = {
  engineering: [
    'Reviewing PR #{{rand}}',
    'Fixing bug in pipeline',
    'Running tests',
    'Deploying to staging',
  ],
  research: [
    'Scanning arxiv feed',
    'Synthesizing sources',
    'Fact-checking draft',
    'Building knowledge graph',
  ],
  comms: [
    'Drafting reply to user',
    'Summarizing thread',
    'Routing inbound request',
    'Composing follow-up',
  ],
  logistics: [
    'Scheduling delivery run',
    'Reconciling inventory',
    'Flagging delay on order #{{rand}}',
    'Updating tracking',
  ],
  strategy: [
    'Mapping Q3 priorities',
    'Evaluating risk surface',
    'Reviewing competitive intel',
    'Drafting roadmap',
  ],
};

const SKILL_TO_ZONE = {
  coding: 'engineering',
  research: 'research',
  communications: 'comms',
  logistics: 'logistics',
  planning: 'strategy',
};

const AUTONOMOUS_THINK_POLL_INTERVAL_MS = 5000;
const AUTONOMOUS_THINK_COOLDOWN_MS = {
  2: 60000,
  3: 15000,
};

export function pickTask(agent, zone) {
  const tasks = ZONE_TASKS[zone.id] || ZONE_TASKS.engineering;
  const task = tasks[Math.floor(Math.random() * tasks.length)];
  return task.replace(/\{\{rand\}\}/g, String(Math.floor(100 + Math.random() * 900)));
}

function pickZone(agent) {
  const skillName = agent.skills?.[0]?.name;
  const preferredZoneId = skillName ? SKILL_TO_ZONE[skillName] : null;

  const candidates = ZONES.filter((zone) => zone.id !== agent.currentZoneId && zone.id !== 'life');
  if (candidates.length === 0) return ZONES[0];

  if (preferredZoneId) {
    const preferred = candidates.find((zone) => zone.id === preferredZoneId);
    if (preferred) return preferred;
  }

  return candidates[Math.floor(Math.random() * candidates.length)];
}

function pickLifeActivity(agent) {
  const currentHour = new Date().getHours();
  const preferred = agent.lifeSim?.baseActivities || Object.keys(ACTIVITIES);

  const weighted = preferred
    .filter((key) => ACTIVITIES[key])
    .map((key) => {
      const act = ACTIVITIES[key];
      const biasMatch = act.timeOfDayBias.includes(currentHour);
      return { key, weight: biasMatch ? 3 : 1 };
    });

  const totalWeight = weighted.reduce((sum, item) => sum + item.weight, 0);
  let roll = Math.random() * totalWeight;
  for (const item of weighted) {
    roll -= item.weight;
    if (roll <= 0) return item.key;
  }
  return weighted[weighted.length - 1]?.key || 'sleep';
}

function pickWorkstationInZone(zone, agentId) {
  const allAgents = useWorldStore.getState().agents;
  const workstation = zone.workstations[Math.floor(Math.random() * zone.workstations.length)];
  const ox = (Math.random() - 0.5) * 1.2;
  const oz = (Math.random() - 0.5) * 1.2;
  let tx = workstation[0] + ox;
  let tz = workstation[1] + oz;

  const occupied = allAgents.filter(
    (agent) => agent.id !== agentId && agent.currentZoneId === zone.id
  );
  for (let tries = 0; tries < 5; tries++) {
    const tooClose = occupied.some((agent) => {
      const tp = agent.targetPosition || agent.position;
      const dx = tp[0] - tx;
      const dz = tp[2] - tz;
      return Math.sqrt(dx * dx + dz * dz) < 0.8;
    });
    if (!tooClose) break;
    tx = workstation[0] + (Math.random() - 0.5) * 1.2;
    tz = workstation[1] + (Math.random() - 0.5) * 1.2;
  }

  return [tx, tz];
}

export default function useZoneBehavior() {
  const timersRef = useRef(new Map());

  useEffect(() => {
    const timers = timersRef.current;

    const dropCooldowns = new Map();

    window.__setDropCooldown = (agentId, ms = 10000) => {
      dropCooldowns.set(agentId, Date.now() + ms);
    };

    function runTick(agentId) {
      const agent = useWorldStore.getState().agents.find((entry) => entry.id === agentId);
      if (!agent) return;

      const hoveredId = useWorldStore.getState().hoveredAgentId;
      if (agent.id === hoveredId || agent.state === 'dragged') return;

      const activeTask = useWorldStore.getState().activeTasks?.[agentId];
      if (activeTask?.status === 'running') return;

      if (agent.state === 'walking') return;

      const cooldown = dropCooldowns.get(agentId);
      if (cooldown && Date.now() < cooldown) return;
      if (cooldown) dropCooldowns.delete(agentId);

      if (agent.mode === 'work' && !activeTask) {
        const lifeZone = ZONES.find((zone) => zone.id === 'life');
        if (!lifeZone) return;
        const [tx, tz] = pickWorkstationInZone(lifeZone, agentId);
        useWorldStore.getState().updateAgentState(agentId, {
          state: 'walking',
          targetPosition: [tx, 0, tz],
          currentZoneId: 'life',
          currentTask: '🏠 Heading home',
          mode: 'life',
          activity: null,
          locationTag: null,
          _persistOnArrival: true,
        });
        return;
      }

      if (agent.mode !== 'work' && (agent.state === 'idle' || agent.state === 'busy')) {
        if (agent.currentZoneId !== 'life') {
          const lifeZone = ZONES.find((zone) => zone.id === 'life');
          if (!lifeZone) return;
          const [tx, tz] = pickWorkstationInZone(lifeZone, agentId);
          useWorldStore.getState().updateAgentState(agentId, {
            state: 'walking',
            targetPosition: [tx, 0, tz],
            currentZoneId: 'life',
            mode: 'life',
            _persistOnArrival: true,
          });
        }
      }
    }

    function startTick(agentId) {
      runTick(agentId);

      const interval = 20000 + Math.random() * 100000;
      const id = setInterval(() => runTick(agentId), interval);
      timers.set(agentId, id);
    }

    const unsub = useWorldStore.subscribe((state) => {
      for (const agent of state.agents) {
        if (!timers.has(agent.id)) {
          startTick(agent.id);
        }
      }
    });

    for (const agent of useWorldStore.getState().agents) {
      if (!timers.has(agent.id)) {
        startTick(agent.id);
      }
    }

    const lifeSimTimers = new Map();
    const lastTickTime = new Map();

    function startLifeSimTick(agentId) {
      if (lifeSimTimers.has(agentId)) return;
      lastTickTime.set(agentId, Date.now());

      const id = setInterval(() => {
        const agent = useWorldStore.getState().agents.find((entry) => entry.id === agentId);
        if (!agent) return;

        if (agent.mode !== 'life') {
          lastTickTime.set(agentId, Date.now());
          return;
        }

        const now = Date.now();
        const realDeltaMs = now - (lastTickTime.get(agentId) || now);
        lastTickTime.set(agentId, now);

        const deltaMin = realDeltaMs / 60000;

        const lifeSim = useWorldStore.getState().lifeSimState[agentId];
        if (lifeSim && lifeSim.simMin + deltaMin >= 1440) {
          useWorldStore.getState().resetDaySlots(agentId);
        }

        useWorldStore.getState().tickAgentLifeSim(agentId, deltaMin);

        const agentNow = useWorldStore.getState().agents.find((entry) => entry.id === agentId);
        if (agentNow?._pendingTravel) {
          const pending = agentNow._pendingTravel;
          useWorldStore.getState().updateAgentState(agentId, { _pendingTravel: undefined });

          if (pending.fromCoords && pending.toCoords) {
            (async () => {
              try {
                const { getRoute, getTravelProfile } = await import('../services/mapboxPlaces.js');
                const route = await getRoute(pending.fromCoords, pending.toCoords, 'walking');
                useWorldStore.getState().startTravel(agentId, pending.fromCoords, pending.toCoords, {
                  placeName: pending.placeName,
                  travelActivity: route ? getTravelProfile(route.distance) : 'walking',
                  routeGeometry: route?.geometry || null,
                  duration: route?.duration || null,
                });
              } catch {
                useWorldStore.getState().startTravel(agentId, pending.fromCoords, pending.toCoords, {
                  placeName: pending.placeName,
                  travelActivity: 'walking',
                });
              }
            })();
          } else if (pending.from && pending.to) {
            useWorldStore.getState().startTravel(
              agentId,
              { lat: 34.04, lng: -118.25 }, { lat: 34.04, lng: -118.25 },
              { placeName: pending.to, travelActivity: pending.mode || 'walking' }
            );
          }
        }

        const travel = useWorldStore.getState().agentTravel?.[agentId];
        if (travel?.status === 'traveling') {
          const elapsed = Date.now() - travel.travelStartTime;
          if (elapsed >= travel.travelDuration) {
            useWorldStore.getState().completeTravel(agentId);
          }
        }
      }, 1000);

      lifeSimTimers.set(agentId, id);
    }

    const lifeUnsub = useWorldStore.subscribe((state) => {
      for (const agent of state.agents) {
        if (!lifeSimTimers.has(agent.id)) startLifeSimTick(agent.id);
      }
    });

    for (const agent of useWorldStore.getState().agents) {
      if (!lifeSimTimers.has(agent.id)) startLifeSimTick(agent.id);
    }

    const thinkTimers = new Map();

    function startThinkTimer(agentId) {
      if (thinkTimers.has(agentId)) return;

      const id = setInterval(async () => {
        const agent = useWorldStore.getState().agents.find((entry) => entry.id === agentId);
        if (!agent) return;

        const level = agent.autonomyLevel || 0;
        if (level < 2) return;

        const thinkState = useWorldStore.getState().autonomousThinkState[agentId];
        if (thinkState?.isThinking) return;
        const cooldown = AUTONOMOUS_THINK_COOLDOWN_MS[level] || AUTONOMOUS_THINK_COOLDOWN_MS[2];
        if (thinkState?.lastThinkTime && Date.now() - thinkState.lastThinkTime < cooldown) return;

        if (agent.mode === 'work') return;
        const activeTask = useWorldStore.getState().activeTasks?.[agentId];
        if (activeTask?.status === 'running') return;

        try {
          const { autonomousThink } = await import('../services/autonomousThink.js');
          await autonomousThink(agentId);
        } catch (err) {
          console.error(`[think-timer] ${agent.name} error:`, err);
        }
      }, AUTONOMOUS_THINK_POLL_INTERVAL_MS);

      thinkTimers.set(agentId, id);
    }

    const thinkUnsub = useWorldStore.subscribe((state) => {
      for (const agent of state.agents) {
        if (!thinkTimers.has(agent.id)) startThinkTimer(agent.id);
      }
    });

    for (const agent of useWorldStore.getState().agents) {
      if (!thinkTimers.has(agent.id)) startThinkTimer(agent.id);
    }

    return () => {
      unsub();
      lifeUnsub();
      thinkUnsub();
      for (const id of timers.values()) clearInterval(id);
      for (const id of lifeSimTimers.values()) clearInterval(id);
      for (const id of thinkTimers.values()) clearInterval(id);
      timers.clear();
      lifeSimTimers.clear();
      thinkTimers.clear();
    };
  }, []);
}
